import type { RetryOptions } from '../interfaces/RetryOptions';
import type { AsyncResult, AppError } from '../../error/types';
import { err, ok } from '../../error/functions';

const DEFAULT_OPTIONS: Omit<Required<RetryOptions>, 'getDelayMs'> = {
  maxAttempts: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  factor: 2,
  retryableErrors: [
    'ECONNRESET',
    'ETIMEDOUT',
    'ECONNREFUSED',
    'EPIPE',
    'EHOSTUNREACH',
    'ENETUNREACH',
    'ENOTFOUND',
    429,
    500,
    502,
    503,
    504,
  ],
};

/**
 * Determines if an AppError (or error-like object) should trigger a retry.
 * Checks metadata.status, metadata.thrownError, and error object shape.
 */
function isRetryableAppError(error: AppError | unknown, retryableErrors: Array<string | number>): boolean {
  const errorObj = error as Record<string, unknown>;
  if (!errorObj || typeof errorObj !== 'object') return false;

  // AppError with metadata.status (e.g. HTTP status from response)
  const status = (errorObj.metadata as Record<string, unknown>)?.status;
  if (typeof status === 'number' && retryableErrors.includes(status)) return true;

  // AppError with metadata.thrownError (e.g. from tryCatchAsync)
  const thrown = (errorObj.metadata as Record<string, unknown>)?.thrownError;
  if (thrown && typeof thrown === 'object') {
    const t = thrown as Record<string, unknown>;
    if (typeof t.status === 'number' && retryableErrors.includes(t.status)) return true;
    if (typeof t.code === 'string' && retryableErrors.includes(t.code)) return true;
  }

  // Direct error object (status, code)
  if (typeof (errorObj as { status?: number }).status === 'number' && retryableErrors.includes((errorObj as { status: number }).status))
    return true;
  if (typeof (errorObj as { code?: string }).code === 'string' && retryableErrors.includes((errorObj as { code: string }).code))
    return true;

  // Fallback: check error message for known error code strings
  const message = (errorObj as { message?: string }).message ?? (errorObj as AppError).errorMessage;
  if (typeof message === 'string' && retryableErrors.some(code => typeof code === 'string' && message.includes(code)))
    return true;

  return false;
}

/**
 * @group Functions
 * @summary
 * <blockquote>Executes a Result-returning function with exponential backoff retry logic.</blockquote>
 *
 * Retries when the function returns `err` and the error is retryable (e.g. status in retryableErrors).
 * Does not use throw/catch; use this when the operation returns Result natively.
 *
 * @example
 * ```typescript
 * const result = await withRetryResult(
 *   async () => {
 *     const response = await fetch(url);
 *     if (!response.ok) return err({ metadata: { status: response.status } });
 *     return ok(response);
 *   },
 *   { maxAttempts: 3 }
 * );
 * ```
 */
export async function withRetryResult<T>(
  fn: () => AsyncResult<T, AppError>,
  options: RetryOptions = {}
): AsyncResult<T, AppError> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let attempt = 1;
  let delay = config.initialDelay;

  while (attempt <= config.maxAttempts) {
    const result = await fn();

    if (result.isOk) {
      return result;
    }

    const error = result.getFail();

    if (
      attempt === config.maxAttempts ||
      !isRetryableAppError(error, config.retryableErrors)
    ) {
      return err({
        errorCode: 'RETRY_FAILED',
        errorMessage: (error as AppError).errorMessage ?? String(error),
        metadata: { ...(error as AppError).metadata, lastError: error },
      });
    }

    const delayMs = config.getDelayMs?.(attempt, error) ?? delay;

    await new Promise(resolve => setTimeout(resolve, delayMs));

    delay = Math.min(delay * config.factor, config.maxDelay);
    attempt++;
  }

  return err({
    errorCode: 'RETRY_EXHAUSTED',
    errorMessage: 'Retry attempts exhausted',
  });
}
